import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  PropsWithChildren,
} from "react";
import {
  initFetch,
  IState,
  onFetchFailure,
  onFetchSuccess,
  useFetch,
} from "./features/profileAccountHook";
import SecurityService from "@services/Security.service";
import FundsService from "@services/Funds.service";
import ProfileService from "@services/Profile.service";
import MarketingService from "@services/Marketing.service";
import { AxiosError, AxiosResponse } from "axios";
import { getAxiosErrorMessage } from "@features/utils/functions";
import { pushErrorNotify } from "@appComponents/Notification/Notification";
import { IWalletDetails } from "@market/interfaces/IWallet";
import Big from "big.js";
import { RouteComponentProps } from "react-router-dom";

const big = Big();

export type AccountPageProps = {
  investorId: number;
  slug: string;
} & RouteComponentProps;

export type AccountPageContextType = {
  state: IState;
  dispatch: any;
  is2FALoading: boolean;
  is2FAEnabled: boolean;
  is2FALocked: boolean;
  isDataLoading: boolean;
  fetch2FAStatus: () => void;
  fetchProfileData: () => void;
  setIs2FALoading: (val: boolean) => void;
  setIs2FAEnabled: (val: boolean) => void;
  setIs2FALocked: (val: boolean) => void;
  setIsDataLoading: (val: boolean) => void;
};

const AccountPageContext = createContext<AccountPageContextType | undefined>(
  undefined
);

export const AccountPageProvider = ({
  children,
  investorId,
}: PropsWithChildren<AccountPageProps>) => {
  const [state, dispatch] = useFetch();
  const [is2FALoading, setIs2FALoading] = useState(true);
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [is2FALocked, setIs2FALocked] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);

  const fetch2FAStatus = () => {
    setIs2FALoading(true);
    SecurityService.twoFaStatus().then((resp) => {
      if (typeof resp?.data?.locked === "boolean") {
        setIs2FALocked(resp.data.locked);
      }
      if (typeof resp?.data?.enabled === "boolean") {
        setIs2FAEnabled(resp.data.enabled);
      }
      setIs2FALoading(false);
    });
  };

  const fetchProfileData = () => {
    const walletsPromise = FundsService.getWallets(investorId);
    const profileDataPromise = ProfileService.fetchProfileData();
    const userDataPromise = ProfileService.fetchUserData();
    const marketingPromise = MarketingService.getNotificationSetting();

    dispatch(initFetch());

    Promise.all([
      walletsPromise,
      profileDataPromise,
      userDataPromise,
      marketingPromise,
    ])
      .then(
        ([
          walletsData,
          profileData,
          userData,
          newsletterData,
        ]: AxiosResponse[]) => {
          const canCloseAccount = walletsData.data.every(
            (wallet: IWalletDetails) => big(wallet.balance).eq(0)
          );
          const newsletterEnabled = Boolean(
            newsletterData?.data?.newsletter_enabled
          );
          dispatch(
            onFetchSuccess(profileData.data, userData.data, canCloseAccount, {
              newsletter_enabled: newsletterEnabled,
            })
          );
          if (isDataLoading) {
            setIsDataLoading(false);
          }
        }
      )
      .catch((error: AxiosError) => {
        dispatch(onFetchFailure());
        pushErrorNotify(getAxiosErrorMessage(error));
      });
  };

  useEffect(() => {
    fetchProfileData();
    fetch2FAStatus();
  }, []);

  return (
    <AccountPageContext.Provider
      value={{
        state,
        dispatch,
        is2FALoading,
        is2FAEnabled,
        is2FALocked,
        isDataLoading,
        fetch2FAStatus,
        fetchProfileData,
        setIs2FAEnabled,
        setIs2FALoading,
        setIs2FALocked,
        setIsDataLoading,
      }}
    >
      {children}
    </AccountPageContext.Provider>
  );
};

export const useAccountPageContext = () => {
  const context = useContext(AccountPageContext);
  if (!context) {
    throw new Error(
      "useAccountPageContext must be used within an AccountPageProvider"
    );
  }
  return context;
};
